import { Fragment, useEffect, useState } from "react";
import { withRouter, Redirect } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import { notification } from "antd";
import UserApi from "../../sdk/api/User";
import PublicLayout from "../Layouts/Public";
import AuthLayout from "../Layouts/Auth";
import MainLayout from "../Layouts/Main";

const Layouts = {
  public: PublicLayout,
  auth: AuthLayout,
  main: MainLayout,
};

const mapStateToProps = ({ user }) => ({ user });

const Layout = ({ user, children, title, location: { pathname, search } }) => {

  const dispatch = useDispatch();
  const [isMounted, setIsMounted] = useState(false);
  const [isFetchingUserData, setIsFetchingUserData] = useState(true);
  // Email verification
  const [isEmailVerified, setIsEmailVerified] = useState(true);
  const [checkingVerification, setCheckingVerification] = useState(true);

  // MFA
  const [hasExistingMfa, setHasExistingMfa] = useState(false);
  const [checkingMfa, setCheckingMfa] = useState(true);

  useEffect(() => {
		setIsMounted(true);
		return () => {
			setIsMounted(false);
		};
	}, []);

  // Verify session, check if email is verified, check if MFA is setup
  useEffect(() => {
    const verifySession = async () => {
      try {
        const res = await UserApi.GetOwnUserInfo();

        if (res) {
          const {
            id,
            fullName,
            firstName,
            lastName,
            email,
            phoneNumber: phone,
            role,
            emailVerified,
            mfaPhoneNumber
          } = res.userData;
    
          dispatch({
            type: "user/SET_STATE",
            payload: {
              id,
              fullName,
              firstName,
              lastName,
              email,
              phoneNumber: phone,
              role,
              authorized: true,
              emailVerified,
              mfaPhoneNumber
            }
          });

          if (emailVerified) {
            setIsEmailVerified(emailVerified);
          }
          setCheckingVerification(false);

          if (id !== '' && emailVerified) {
            try {
              setCheckingMfa(true);

              const enrolledFactors = res.firebaseUser?.multiFactor?.enrolledFactors || [];
              setHasExistingMfa(enrolledFactors.length > 0);
            } catch (error) {
              notification.error({
                message: 'Cannot fetch user information'
              });
            } finally {
              setCheckingMfa(false);
            }
          } else {
            setCheckingMfa(false);
            setHasExistingMfa(true);
          }
        }
      } catch (error) {
        notification.error({
          message: "Error fetching user data"
        });
      } finally {
        setIsFetchingUserData(false);
      }
    };

    if (isMounted && !user.authorized && isFetchingUserData) verifySession();
  }, [isMounted, user.authorized, isFetchingUserData, dispatch]);

  // Fetch the full user directory ONCE per session, as soon as the person
  // is authorized - NOT inside EditHistory.js (which is a shared component
  // mounted in many places - Patient, Group Number, Insurance Company, Fee
  // Schedule, Form, PDF tabs - so it having its own fetch would risk
  // several near-simultaneous fetches racing each other, and every mount
  // point would need to reason about "did someone already fetch this").
  // Layout is the single outermost wrapper for the whole app (see App.js -
  // it wraps both auth and main layouts), so it's the natural place for
  // reference data every part of the app needs.
  //
  // No redux-thunk in this app, so the request/success/failure dispatch
  // sequence lives here in the component rather than in an async action
  // creator - but the STATE SHAPE still follows Redux's own documented
  // async-state pattern (usersStatus: 'idle'|'loading'|'succeeded'|
  // 'failed', not a boolean flag) so a second fetch can't start while one
  // is already in flight, and a failed fetch is distinguishable from one
  // that never ran.
  useEffect(() => {
    const fetchUserDirectory = async () => {
      dispatch({ type: "user/FETCH_USERS_REQUEST" });

      try {
        const result = await UserApi.GetUserList(user.id);

        dispatch({
          type: "user/FETCH_USERS_SUCCESS",
          payload: result.usersArray,
        });
      } catch (error) {
        dispatch({
          type: "user/FETCH_USERS_FAILURE",
          payload: error.message || "Failed to load the user directory",
        });
        notification.warning({
          message: "There was a problem loading the user directory"
        });
      }
    };

    if (isMounted && user.authorized && user.usersStatus === 'idle') {
      fetchUserDirectory();
    }
  }, [isMounted, user.authorized, user.usersStatus, user.id, dispatch]);

  // Layout Rendering
  const getLayout = () => {
    if (pathname === "/") {
      return "public";
    }
    if (/^\/auth(?=\/|$)/i.test(pathname)) {
      return "auth";
    }
    return "main";
  };

  const Container = Layouts[getLayout()];
  const isAuthLayout = getLayout() === "auth";

  const BootstrappedLayout = () => {
    // show loader when user in check authorization process, not authorized yet and not on login pages
    if (isFetchingUserData && !user.authorized && !isAuthLayout) {
      return null;
    }

    // The email of the user is not yet verified
    if (
      !checkingVerification && 
      !isEmailVerified && 
      user.authorized &&
      pathname !== "/auth/email/verify"
    ) {
      return <Redirect to="/auth/email/verify" />;
    }

    // The email of the user is not yet verified
    if (
      !checkingVerification && 
      !checkingMfa &&
      isEmailVerified && 
      user.authorized &&
      !hasExistingMfa &&
      !isAuthLayout &&
      pathname !== "/auth/mfa/set-up"
    ) {
      return <Redirect to="/auth/mfa/setup" />;
    }

    // redirect to login page if current is not login page and user not authorized
    if (!isAuthLayout && !user.authorized) {
      return <Redirect to="/auth/login" />;
    }

    // in other case render previously set layout
    return <Container>{children}</Container>;
  };

  return (
    <Fragment>
      <Helmet title={title} />
      {BootstrappedLayout()}
    </Fragment>
  );
};

export default withRouter(connect(mapStateToProps)(Layout));
